import React from 'react';

import Select from '../../../../../components/input/Select';
import OpenUserDetail from '../../../../../components/link/OpenUserDetail';
import TableFilterInput from '../../../../../components/table/TableFilterInput';
import DateRangePicker from '../../../../../components/datePicker/DateRangePicker';
import { setClearDash, setColumnWidth, setPhoneFormat } from '../../../../../lib/common';
import { setDateFormat } from '../../../../../lib/moment';
import {
  ACCOUNT_USER_DEVICE_PUSH_SELECT_LIST,
  ACCOUNT_USER_DEVICE_PUSH_STATUS_TEXT,
  ACCOUNT_USER_LIST_STATUS_SELECT_LIST,
  ACCOUNT_USER_LIST_TYPE_TEXT,
  ACCOUNT_USER_LOGIN_TYPE_SELECT_LIST,
  ACCOUNT_USER_STATUS_TEXT,
} from '../../../../../constants/service/app/accountUserConstants';
import { AMONDZ_MEMBER_TYPE, DATE_FORMAT } from '../../../../../constants/service/common/utilConstants';
import SendMessageService from '../../../../../containers/app/common/SendMessageService';
import { ColumnParamsType, FixedTableColumn } from './types/column';
import { LoginType, UserStatusType } from './types/accountUser';
import { DateRageType } from '../../../../../types/common/date';
import { UserGradeIdType, UserGradeResponseType } from '../../detail/AccountUserDetailGrade/types';

/**
 *  유저 리스트 컬럼
 */
const columns = (data: ColumnParamsType): FixedTableColumn[] => {
  const { list, filterOption, gradeList, onFilter } = data;

  return [
    {
      accessor: 'id',
      Header: '회원번호',
      sortable: true,
      filterable: true,
      fixed: 'left',
      Cell: (row) => <OpenUserDetail text={row.value} userId={row.value} />,
      Filter: () => <TableFilterInput option={filterOption.id} onFilter={(id: number) => onFilter({ id })} />,
    },
    {
      accessor: 'status',
      Header: '계정상태',
      sortable: false,
      filterable: true,
      fixed: 'left',
      width: 170,
      Cell: (row) => ACCOUNT_USER_STATUS_TEXT[row.value] || '-',
      Filter: () => (
        <Select
          list={ACCOUNT_USER_LIST_STATUS_SELECT_LIST}
          onChange={(status: UserStatusType) => onFilter({ status })}
          defaultValue={filterOption.status}
          value={filterOption.status}
          isTable={true}
          size="large"
        />
      ),
    },
    {
      accessor: 'name',
      Header: '이름',
      filterable: true,
      fixed: 'left',
      width: setColumnWidth(list, 'name'),
      Filter: () => <TableFilterInput option={filterOption.name} onFilter={(name: string) => onFilter({ name })} />,
    },
    {
      accessor: 'account',
      Header: '아이디',
      filterable: true,
      resizable: true,
      fixed: 'left',
      width: setColumnWidth(list, 'account'),
      Filter: () => (
        <TableFilterInput option={filterOption.account} onFilter={(account: string) => onFilter({ account })} />
      ),
    },
    {
      accessor: 'loginType',
      Header: '로그인 타입',
      sortable: false,
      filterable: true,
      width: 120,
      Cell: (row: { value: LoginType }) => ACCOUNT_USER_LIST_TYPE_TEXT[row.value] || '-',
      Filter: () => (
        <Select
          list={ACCOUNT_USER_LOGIN_TYPE_SELECT_LIST}
          onChange={(loginType: LoginType) => onFilter({ loginType })}
          defaultValue={filterOption.loginType}
          value={filterOption.loginType}
          isTable={true}
          size="large"
        />
      ),
    },
    {
      accessor: 'cellPhone',
      Header: '핸드폰',
      filterable: true,
      width: setColumnWidth(list, 'cellPhone'),
      Cell: (row) => (
        <SendMessageService
          text={setPhoneFormat(row.value)}
          memberType={AMONDZ_MEMBER_TYPE.USER}
          targetId={row.original.id}
        />
      ),
      Filter: () => (
        <TableFilterInput
          option={filterOption.cellPhone}
          onFilter={(cellPhone: string) =>
            onFilter({
              cellPhone: setClearDash(cellPhone),
            })
          }
        />
      ),
    },
    {
      accessor: 'email',
      Header: '이메일',
      resizable: true,
      filterable: true,
      width: setColumnWidth(list, 'email'),
      Filter: () => <TableFilterInput option={filterOption.email} onFilter={(email: string) => onFilter({ email })} />,
    },
    {
      accessor: 'referralCode',
      Header: '레퍼럴 코드',
      resizable: true,
      filterable: true,
      width: setColumnWidth(list, 'referralCode'),
      Filter: () => (
        <TableFilterInput
          option={filterOption.referralCode}
          onFilter={(referralCode: string) => onFilter({ referralCode })}
        />
      ),
      Cell: (row) => row.value,
    },
    {
      accessor: 'smsReceive',
      Header: 'sms 푸시동의',
      filterable: true,
      width: 100,
      Cell: (row: { value: 0 | 1 }) => ACCOUNT_USER_DEVICE_PUSH_STATUS_TEXT[row.value] || '-',
      Filter: () => (
        <Select
          list={ACCOUNT_USER_DEVICE_PUSH_SELECT_LIST}
          onChange={(smsReceive: 0 | 1) => onFilter({ smsReceive })}
          defaultValue={filterOption.smsReceive}
          value={filterOption.smsReceive}
          isTable={true}
          size="large"
        />
      ),
    },
    {
      accessor: 'userGrade',
      Header: '등급',
      filterable: true,
      width: 100,
      Cell: (row: { value: UserGradeResponseType }) => gradeList.find((grade) => row.value.grade === grade.id)?.name,
      Filter: () => (
        <Select
          list={gradeList}
          onChange={(grade: UserGradeIdType) => onFilter({ grade })}
          defaultValue={filterOption.grade}
          value={filterOption.grade}
          isTable={true}
          size="large"
        />
      ),
    },
    {
      accessor: 'createDate',
      Header: '가입일자',
      sortable: true,
      filterable: true,
      width: 320,
      Cell: (row) => setDateFormat(row.value, DATE_FORMAT.YMD_DASH_FORMAT),
      Filter: () => (
        <DateRangePicker
          value={{ startDate: filterOption.fromCreateDate, endDate: filterOption.toCreateDate }}
          outFormat={DATE_FORMAT.YMD_DASH_FORMAT}
          onSelect={(data: DateRageType) => onFilter({ fromCreateDate: data.startDate, toCreateDate: data.endDate })}
        />
      ),
    },
  ];
};

export default columns;
